// ---------------------------------------------------------------------------
// Local preferences. Persisted.
// ---------------------------------------------------------------------------

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'dark' | 'light' | 'system';

interface SettingsState {
  theme: Theme;
  /**
   * Also honoured for the clock's under-20% pulse, for photosensitivity.
   *
   * **`null` means nobody has decided yet**, and it is the state a fresh device
   * starts in. The OS preference is read exactly once, on the first render that
   * needs an answer, written here, and never consulted again — see
   * `prefersReducedMotion`. That is a deliberate change from ORing the two
   * together forever, which meant a player who turned this OFF in the app still
   * got reduced motion because their laptop said so, with no way to reach the
   * setting that was overruling them.
   *
   * The consequence to keep in mind: after the seed, the machine changing its
   * mind does nothing. That is the point. A device preference is a starting
   * guess about a person, and once they have answered for themselves the guess
   * has been superseded.
   */
  reducedMotion: boolean | null;
  /**
   * Off by default even when signed in as the admin account. Playing a normal
   * game shows no privileged information until this is flipped, and a persistent
   * badge appears while it is on so there is never ambiguity.
   */
  adminMode: boolean;
  adminSessionToken: string | null;
  showConnectionDetail: boolean;
  /** Preferred numbers input surface, once the prototype settles the question. */
  numbersInput: 'builder' | 'typed';

  setTheme: (theme: Theme) => void;
  setReducedMotion: (reducedMotion: boolean) => void;
  setAdminMode: (adminMode: boolean) => void;
  setAdminSessionToken: (token: string | null) => void;
  setShowConnectionDetail: (show: boolean) => void;
  setNumbersInput: (mode: 'builder' | 'typed') => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      theme: 'dark',
      reducedMotion: null,
      adminMode: false,
      adminSessionToken: null,
      showConnectionDetail: false,
      numbersInput: 'builder',

      setTheme: (theme) => set({ theme }),
      setReducedMotion: (reducedMotion) => set({ reducedMotion }),
      setAdminMode: (adminMode) => set({ adminMode }),
      setAdminSessionToken: (adminSessionToken) => set({ adminSessionToken }),
      setShowConnectionDetail: (showConnectionDetail) => set({ showConnectionDetail }),
      setNumbersInput: (numbersInput) => set({ numbersInput }),
    }),
    {
      name: 'countdown-settings',
      version: 2,
      /**
       * v1 stored `reducedMotion: false` for everybody, because the flag was
       * only ever an ADDITION to the OS preference — `false` meant "nothing
       * extra", not "this person wants motion". Carrying that across as a
       * decision would silently overrule every existing player whose machine
       * asks for reduced motion, which is the exact bug this version fixes.
       *
       * So a stored `false` becomes `null` and gets re-seeded from the machine
       * once. A stored `true` is kept: that one WAS a decision, made in the
       * only control the old build offered.
       */
      migrate: (persisted, version) => {
        const state = persisted as Partial<SettingsState> | undefined;
        if (state === undefined) return state as never;
        if (version < 2 && state.reducedMotion === false) {
          return { ...state, reducedMotion: null } as never;
        }
        return state as never;
      },
    },
  ),
);

/**
 * The one answer, and the only place the machine is ever asked.
 *
 * On a device that has never answered, this reads `prefers-reduced-motion`,
 * WRITES it, and returns it. Every call after that reads the stored value.
 * Seeding through the setter rather than returning a computed value on the fly
 * is what makes "asked once" true rather than aspirational: after the first
 * call there is no `null` left to trigger another read.
 *
 * `index.css` no longer carries `@media (prefers-reduced-motion: reduce)` rules
 * of its own. It keys entirely off `data-reduced-motion` on the root, which is
 * stamped from this value — otherwise a player who turned motion back ON would
 * still be overruled by their OS through the stylesheet, and the setting would
 * appear to do nothing.
 */
export function prefersReducedMotion(): boolean {
  const stored = useSettingsStore.getState().reducedMotion;
  if (stored !== null) return stored;

  const fromMachine =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;
  useSettingsStore.getState().setReducedMotion(fromMachine);
  return fromMachine;
}

export function resolveTheme(theme: Theme): 'dark' | 'light' {
  if (theme !== 'system') return theme;
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}
